import {
  getLocalizedDefaults,
  normalizeTokenName,
  TOKEN_NAME_TO_CANONICAL,
  type SupportedLanguage,
} from '../localized-defaults';
import { PreferencesDateFieldOrder } from '../preferences';
import { MessageType } from '../reminders';
import {
  CalendarData,
  ConfirmationLinkData,
  EventData,
  MessageTemplatesContainer,
  MessageTemplateVariables,
  PreferencesData,
  ResolvedTemplateVariables,
  UserData,
} from './message-builder.interface';

export const SAMPLE_CONFIRMATION_LINK = {
  id: '1',
  linkUrl: 'https://www.smsreminder.co/confirmation/ZwhFHBRK2wq9sM6KGME_M',
};

export const TEMPLATE_TOKEN_PATTERN = /\{([^}]+)\}/g;

const MAX_LENGTH = 320;
const LINK_LABEL_MAX_LENGTH = 40;
const LINK_LABEL_BREAKS = ['.', '!', '?', '\n'];

const DEFAULT_MESSAGE_TEMPLATES = {
  confirmation_message: (variables: MessageTemplateVariables) =>
    [
      'Confirmed:',
      variables.title ? ' {title}' : ' your appointment',
      variables.from ? ' with {from}' : '',
      variables.location ? ' at {location}' : '',
      variables.date ? ' on {date}' : '',
      variables.time ? ' at {time}' : '',
      '.',
    ].join(''),
  reminder_message: (variables: MessageTemplateVariables) =>
    [
      'Reminder:',
      variables.title ? ' {title}' : ' your appointment',
      variables.from ? ' with {from}' : '',
      variables.location ? ' at {location}' : '',
      variables.date ? ' on {date}' : '',
      variables.time ? ' at {time}' : '',
      '.',
    ].join(''),
  follow_up_message: (variables: MessageTemplateVariables) =>
    ['Thank you', variables.from ? ' from {from}' : '', '!'].join(''),
};

export class MessageBuilder {
  static selectTemplate(
    messageType: MessageType | null | undefined,
    messageTemplates: MessageTemplatesContainer,
    availableVariables: MessageTemplateVariables,
  ): string {
    const sanitize = (text?: string | null) => (text ?? '').trim();
    switch (messageType) {
      case 'confirmation':
        return (
          sanitize(messageTemplates?.confirmation_message) ||
          DEFAULT_MESSAGE_TEMPLATES.confirmation_message(availableVariables)
        );
      case 'follow_up':
        return (
          sanitize(messageTemplates?.follow_up_message) ||
          DEFAULT_MESSAGE_TEMPLATES.follow_up_message(availableVariables)
        );
      default: // use the 'reminder' message template
        return (
          sanitize(messageTemplates?.reminder_message) ||
          DEFAULT_MESSAGE_TEMPLATES.reminder_message(availableVariables)
        );
    }
  }

  static build(
    messageTemplate: string,
    templateVariables: MessageTemplateVariables,
    confirmationLink?: ConfirmationLinkData | null,
    language: SupportedLanguage = 'en',
  ): string {
    if (!messageTemplate) {
      return '';
    }

    const message = this.substituteTemplateTokens(
      messageTemplate,
      templateVariables,
    );

    const confirmationText = confirmationLink?.linkUrl
      ? this.buildConfirmationSuffix(confirmationLink.linkUrl, language)
      : '';

    // total length of message + confirmation text must not exceed 320 characters
    // but we can't simply truncate the message, because it would break the confirmation link
    // since we have the confirmation text, we can use it's length to determine how much of the message to keep
    let finalMessage = message + confirmationText;
    if (finalMessage.length > MAX_LENGTH) {
      const messageToKeep = this.truncateKeepingLink(
        message,
        MAX_LENGTH - confirmationText.length,
        templateVariables.videolink,
      );
      finalMessage = messageToKeep + confirmationText;
    }

    return finalMessage;
  }

  // A cut short link is dead, and one stripped of its label ("Join here:") or
  // glued to half a word reads as spam. Keep link, then label, then the text
  // after it; the overflow comes out of the text before the label, at a word.
  private static truncateKeepingLink(
    message: string,
    maxLength: number,
    link: string,
  ): string {
    const linkStart = link ? message.indexOf(link) : -1;
    const linkEnd = linkStart + (link?.length ?? 0);
    if (linkStart === -1 || linkEnd <= maxLength || link.length > maxLength) {
      return message.slice(0, maxLength);
    }

    const before = message.slice(0, linkStart);
    const labelStart = this.findLinkLabelStart(before);
    let room = maxLength - link.length;
    const label = before.slice(Math.max(labelStart, before.length - room));
    room -= label.length;
    const after = message.slice(linkEnd, linkEnd + room);
    room -= after.length;
    const head = this.cutHead(before.slice(0, labelStart), room, label);
    return (head + label).trimStart() + link + after;
  }

  // Cutting at a word drops the space or newline after a sentence break, which
  // would glue the head straight onto the link or its label.
  private static cutHead(text: string, room: number, label: string): string {
    const endsAtBreak = LINK_LABEL_BREAKS.includes(text[text.length - 1]);
    if (text.length <= room || room < 2 || !endsAtBreak || /^\s/.test(label)) {
      return this.cutAtWordBoundary(text, room);
    }
    return `${this.cutAtWordBoundary(text, room - 1)} `;
  }

  private static findLinkLabelStart(before: string): number {
    const sentenceStart =
      Math.max(...LINK_LABEL_BREAKS.map((c) => before.lastIndexOf(c))) + 1;
    const minStart = before.length - LINK_LABEL_MAX_LENGTH;
    if (sentenceStart >= minStart) return sentenceStart;
    const wordStart = before.indexOf(' ', minStart);
    return wordStart === -1 ? minStart : wordStart;
  }

  private static cutAtWordBoundary(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    const cut = text.slice(0, maxLength);
    const lastSpace = cut.lastIndexOf(' ');
    return lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
  }

  // One pass, so a substituted value carrying brace text (a title with
  // "{fecha}") is never rewritten.
  private static substituteTemplateTokens(
    messageTemplate: string,
    templateVariables: MessageTemplateVariables,
  ): string {
    return messageTemplate.replace(
      TEMPLATE_TOKEN_PATTERN,
      (placeholder, rawTokenName) => {
        const token = TOKEN_NAME_TO_CANONICAL.get(
          normalizeTokenName(rawTokenName),
        );
        return token ? (templateVariables[token] ?? '') : placeholder;
      },
    );
  }

  private static buildConfirmationSuffix(
    linkUrl: string,
    language: SupportedLanguage,
  ): string {
    const suffixTemplate =
      getLocalizedDefaults(language).confirmationLinkSuffix;
    return ` ${suffixTemplate.replace('{url}', linkUrl)}`;
  }

  static extractTemplateVariables(
    event: EventData,
    calendar: CalendarData,
    user: UserData,
    preferences: PreferencesData,
    resolved: ResolvedTemplateVariables,
  ): MessageTemplateVariables {
    const { date, time } = this.extractDateTime(
      event,
      calendar,
      user,
      preferences,
    );

    return {
      title: event.title ?? '',
      from: event.sender_name ?? '',
      location: event.location ?? '',
      eventlocation: event.event_location ?? '',
      videolink: resolved.videolink,
      date,
      time,
    };
  }

  private static extractDateTime(
    event: EventData,
    calendar: CalendarData,
    user: UserData,
    preferences: PreferencesData,
  ): { date: string; time: string } {
    const locale = preferences.locale || 'en';
    const dateOrder = preferences.date_field_order || 'MDY';

    const timezoneForDisplay =
      (user.use_event_timezone && event.timezone !== calendar.timezone
        ? event.timezone
        : null) ||
      calendar.timezone_custom ||
      calendar.timezone;

    const eventDate = new Date(Number(event.event_date));

    const date = this.formatDateInLocale(
      eventDate,
      locale,
      dateOrder,
      timezoneForDisplay,
    );

    const time = this.formatTimeInLocale(
      eventDate,
      locale,
      preferences.hour_format === 'h24' ? 'h24' : 'h12',
      timezoneForDisplay,
    );

    return {
      date,
      time: event.all_day ? 'All Day' : time,
    };
  }

  private static formatDateInLocale(
    date: Date,
    locale: string,
    dateFieldOrder: PreferencesDateFieldOrder,
    timezone?: string,
  ) {
    locale = (locale || 'en').replace('_', '-');

    // ISO 8601 format is always YYYY-MM-DD regardless of locale
    if (dateFieldOrder === 'YMD') {
      return date.toISOString().split('T')[0];
    }

    // Get locale-specific formatting to determine separators and conventions
    const formatter = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      timeZone: timezone || 'UTC',
    });

    // Extract the parts from the formatted string
    const parts = formatter.formatToParts(date);
    const yearPart = parts.find((part) => part.type === 'year')?.value || '';
    const monthPart = parts.find((part) => part.type === 'month')?.value || '';
    const dayPart = parts.find((part) => part.type === 'day')?.value || '';

    // Find the separator used in this locale
    const separator =
      parts.find((part) => part.type === 'literal')?.value || '/';

    // Reconstruct the date based on the requested field order
    if (dateFieldOrder === 'DMY') {
      return `${dayPart}${separator}${monthPart}${separator}${yearPart}`;
    }

    // Default to mdy
    return `${monthPart}${separator}${dayPart}${separator}${yearPart}`;
  }

  private static formatTimeInLocale(
    date: Date,
    locale: string,
    hourFormat: 'h24' | 'h12',
    timeZone: string,
  ): string {
    const normalizedLocale = (locale || 'en').replace('_', '-');

    const formatter = new Intl.DateTimeFormat(normalizedLocale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: hourFormat !== 'h24',
      timeZone: timeZone || 'UTC',
    });

    const parts = formatter.formatToParts(date);

    const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
    const minute = parts.find((p) => p.type === 'minute')?.value ?? '';
    const dayPeriod = parts.find((p) => p.type === 'dayPeriod')?.value ?? '';

    if (hourFormat === 'h24') {
      return `${hour}:${minute}`;
    }

    const ampm = dayPeriod.toUpperCase().replace(/\./g, '').replace(/\s/g, '');
    return `${hour}:${minute}${ampm}`;
  }
}
